import breakpoints from '../config/breakpoints'
import { getHookLogicUrl, getCitrusUrl } from '../helpers/GetHookLogicUrl'
import HookLogicHelper from '../helpers/HookLogicHelper'
import mergePdpDataToHookLogic from '../helpers/MergePdpDataToHookLogic'
import hookLogicService from '../services/HookLogicService'
import beaconService from '../services/BeaconService'
import { get } from '../utils/PolyfillLodash'
import { clearHookLogicProducts, handleHookLogic, showHookLogicProducts } from '../redux/hookLogic/hookLogic'

import Logger from '../utils/Logger'

const logger = new Logger()

const getPlatform = function getPlatform() {
  return window.matchMedia(breakpoints.medium).matches ? 'web' : 'mobile'
}

const getHookLogicUserId = function getUserId() {
  const AMCV_MATCH = /AMCV_[0-9A-Z%]+AdobeOrg=([0-9a-zA-Z%\-_]+)/g.exec(document.cookie)
  const S_VI_MATCH = /s_vi=([0-9a-zA-Z%\-|[\]]+)/g.exec(document.cookie)

  if (S_VI_MATCH) {
    return S_VI_MATCH[1]
  }

  return AMCV_MATCH ? AMCV_MATCH[1] : 'unidentified'
}

const getFilterValues = (appliedFilters, filter) => {
  if (!appliedFilters) {
    return
  }
  const appliedFilter = appliedFilters.find(appliedFilter => get(appliedFilter, 'id') === filter)
  return get(appliedFilter, 'values')
}

const getMinMaxPrices = priceValues => {
  if (!priceValues && priceValues.length) {
    return
  }

  const prices = priceValues.reduce((acc, price) => {
    if (price === 'under £5') {
      acc.push(0)
      acc.push(5)
      return acc
    }

    const extractedPrices = price.match(/£(\d+(?:\.\d+)?) - £(\d+(?:\.\d+)?)$/)
    if (!extractedPrices) {
      return acc
    }
    acc.push(...extractedPrices.slice(1))
    return acc
  }, [])

  const parsedPrices = prices.map(price => parseFloat(price))

  return {
    minPrice: Math.min.apply(null, parsedPrices),
    maxPrice: Math.max.apply(null, parsedPrices)
  }
}

const getTaxonomyPath = ({ currentCategory, categoryTree }) => {
  let path = ''
  let filteredCats = []
  if (!!categoryTree[0] && categoryTree[0].attributes !== undefined) {
    filteredCats = buildSortedCategoryTree(categoryTree)
    filteredCats.push(currentCategory.name)
  } else if (currentCategory !== undefined) {
    filteredCats = buildCategoryTree(categoryTree)
    filteredCats.push(currentCategory.name)
  } else {
    filteredCats = buildCategoryTree(categoryTree)
  }
  path = filteredCats.join(' / ').toLowerCase()
  return path
}

const getCannedPath = (keyword, category) =>
  `${keyword
    .split(' ')
    .join(' / ')
    .toLowerCase()}${category ? ` / ${category}` : ''}`

const buildCategoryTree = categoryTree => {
  return categoryTree.filter(tree => !get(tree, 'name', '').match(/master category/i)).map(tree => get(tree, 'name'))
}

const buildSortedCategoryTree = categoryTree => {
  return categoryTree
    .filter(tree => !get(tree, 'attributes.name', '').match(/master category/i))
    .sort((a, b) => {
      const level1 = get(a, 'attributes.level')
      const level2 = get(b, 'attributes.level')
      if (level1 > level2) {
        return 1
      }
      if (level1 < level2) {
        return -1
      }
      return 0
    })
    .map(tree => get(tree, 'attributes.name'))
}

const getExtraFilters = appliedFilters => {
  if (!(appliedFilters && appliedFilters.length)) {
    return
  }

  const excludeFilters = ['category', 'brands', 'price']

  return appliedFilters
    .filter(filter => !excludeFilters.includes(filter.id))
    .reduce((acc, filter) => {
      acc[filter.id] = filter.values.join('|')
      return acc
    }, {})
}

const showHideHookLogic = extraFilters => {
  return !(extraFilters && Object.keys(extraFilters).length)
}

const getAppliedSorts = appliedSorts => {
  if (!appliedSorts) {
    return {}
  }

  const excludeSorts = ['relevance', 'instock']

  return appliedSorts
    .filter(sort => !excludeSorts.includes(sort.name))
    .reduce((acc, appliedSort) => {
      acc[appliedSort.name] = appliedSort.order
      return acc
    }, {})
}

const getPartNumbers = hlResponse => {
  return get(hlResponse, 'body.SearchProductAd', [])
    .map(v => {
      return v.ProductSKU
    })
    .join(',')
}

const isHookLogicResponseValid = res => {
  return (
    get(res, 'body.SearchProductAd') && Array.isArray(res.body.SearchProductAd) && res.body.SearchProductAd.length > 0
  )
}

const isPdpResponseValid = res =>
  res.data && ((res.data.length > 0 && res.data[0].type === 'products') || res.data.type === 'products')

const isProdListValid = productList => {
  return (
    (getPlatform() === 'web' && HookLogicHelper.isProdListDesktopValid(productList)) ||
    (getPlatform() === 'mobile' && HookLogicHelper.isProdListMobileTabletValid(productList))
  )
}

const normalizeHookLogicResponseData = data => {
  const myProductData = get(data, 'SearchProductAd') || get(data, 'ProductAd', [])
  return {
    body: {
      PageBeacon: get(data, 'PageBeacon', ''),
      SearchProductAd: myProductData
    }
  }
}

const fireBeacon = response => {
  if (response && response.body && response.body.PageBeacon) {
    beaconService(response.body.PageBeacon)
  }
}

const enrichProductData = (context, response) => {
  const partNumbers = getPartNumbers(response)
  context.service.read('pdp', { partNumbers }, {}, (error, pdpResponse) => {
    if (error || !isPdpResponseValid(pdpResponse)) {
      return context.reduxStore.dispatch(clearHookLogicProducts())
    }
    const hlData = mergePdpDataToHookLogic({ response, pdpResponse })
    const SearchProductAd = get(hlData, 'SearchProductAd', [])
    if (!isProdListValid(SearchProductAd)) {
      return context.reduxStore.dispatch(clearHookLogicProducts())
    }
    return context.reduxStore.dispatch(handleHookLogic(hlData))
  })
}

const showSkeletonLoaders = (context, response) => {
  const body = get(response, 'body', {})
  const products = body.SearchProductAd || body.ProductAd
  if (products) {
    products.forEach(product => {
      product.Price = null
    })
    context.reduxStore.dispatch(handleHookLogic({ SearchProductAd: products }))
  }
}

export default function executeHookLogic(actionContext, payload) {
  const userId = getHookLogicUserId()
  const brandValues = getFilterValues(payload.appliedFilters, 'brands')
  const priceValues = getFilterValues(payload.appliedFilters, 'price')
  const category = getFilterValues(payload.appliedFilters, 'category')
  const extraFilters = getExtraFilters(payload.appliedFilters)
  const appliedSort = getAppliedSorts(payload.appliedSorts)
  const hookLogicFilters = { ...extraFilters, ...appliedSort }

  const hookLogicParams = {}

  if (brandValues) {
    hookLogicParams.brand = encodeURIComponent(brandValues.join('|'))
  }

  if (priceValues && priceValues.length) {
    const { minPrice, maxPrice } = getMinMaxPrices(priceValues)
    hookLogicParams.minPrice = minPrice
    hookLogicParams.maxPrice = maxPrice
  }

  if (payload.pageType === 'browse' && category && payload.categoryTree) {
    hookLogicParams.taxonomy = encodeURIComponent(getTaxonomyPath(payload))
  }

  if (payload.pageType === 'search' || payload.pageType === 'directory') {
    hookLogicParams.keyword = payload.keyword
  }

  if (payload.pageType === 'canned') {
    hookLogicParams.taxonomy = encodeURIComponent(getCannedPath(payload.keyword, category))
  }

  if (payload.isPlp && hookLogicFilters && hookLogicFilters.clearance) {
    delete hookLogicFilters.clearance
  }

  if (hookLogicFilters && Object.keys(hookLogicFilters).length) {
    hookLogicParams.filters = encodeURIComponent(JSON.stringify(hookLogicFilters))
  }

  hookLogicParams.pgn = payload.currentPage

  const hookLogicUrl = getHookLogicUrl(hookLogicParams, userId, getPlatform(), payload.pageType)
  const citrusUrl = getCitrusUrl(hookLogicParams, userId, getPlatform())
  const hookLogicRequest = hookLogicService(hookLogicUrl)
  const citrusRequest = window.enableCitrus === 'true' ? hookLogicService(citrusUrl) : Promise.resolve({})
  return Promise.all([hookLogicRequest, citrusRequest])
    .then(responses => {
      const hlResponse = normalizeHookLogicResponseData(responses[0])
      const cResponse = normalizeHookLogicResponseData(responses[1])
      fireBeacon(hlResponse)
      fireBeacon(cResponse)
      // clear hooklogic for certain filter combinations
      if (!showHideHookLogic(hookLogicFilters)) {
        return actionContext.reduxStore.dispatch(clearHookLogicProducts())
      }
      // continue if hookLogic response valid and if HL response has 3 results for desktop, 2 mobile
      if (isHookLogicResponseValid(hlResponse) && isProdListValid(get(hlResponse, 'body.SearchProductAd', []))) {
        actionContext.reduxStore.dispatch(showHookLogicProducts())
        showSkeletonLoaders(actionContext, hlResponse)
        return enrichProductData(actionContext, hlResponse)
      }
      if (isHookLogicResponseValid(cResponse) && isProdListValid(get(cResponse, 'body.SearchProductAd', []))) {
        actionContext.reduxStore.dispatch(showHookLogicProducts())
        showSkeletonLoaders(actionContext, cResponse)
        return enrichProductData(actionContext, cResponse)
      }
      return actionContext.reduxStore.dispatch(clearHookLogicProducts())
    })
    .catch(err => {
      logger.error('apiLogger', err, 'Unable to fetch hooklogic.')
      return actionContext.reduxStore.dispatch(clearHookLogicProducts())
    })
}
